/**
 * Theme runtime.
 *
 * Applies a theme by setting `data-theme` on <html>. The actual design
 * tokens for each theme live in `src/styles/theme.css` — this file only
 * decides which theme is active and persists that choice to localStorage.
 *
 * To add a new theme:
 *   1. Add a `[data-theme="<name>"]` block in src/styles/theme.css.
 *   2. Append the name (and label) to THEMES below.
 *   3. The picker UI will pick it up automatically once it reads from THEMES.
 *
 * Usage:
 *   import { applyTheme, getStoredTheme, THEMES } from '@/lib/theme';
 *   applyTheme('dark');                  // apply + persist
 *   applyTheme(getStoredTheme() ?? 'light');
 */

export type ThemeName = 'light' | 'dark' | 'hc';

export interface ThemeOption {
  name: ThemeName;
  label: string;
  description: string;
}

// Light + Dark are exposed to users. The HC (high-contrast) token block still
// exists in global-theme.css and works end-to-end (set data-theme="hc" on
// <html> manually to try it), but it isn't surfaced in the UI yet — uncomment
// the line below when it's ready to ship.
export const THEMES: readonly ThemeOption[] = [
  { name: 'light', label: 'Light', description: 'Clean white surfaces.' },
  { name: 'dark',  label: 'Dark',  description: 'Neutral black dark mode.' },
  // { name: 'hc',    label: 'High contrast', description: 'Black + white for accessibility.' },
] as const;

const STORAGE_KEY = 'gc.theme.v1';
const DEFAULT_THEME: ThemeName = 'light';

const isThemeName = (v: unknown): v is ThemeName =>
  typeof v === 'string' && THEMES.some(t => t.name === v);

/**
 * Apply a theme to the document root and persist the choice.
 * Falls back silently if `window` / `localStorage` aren't available.
 */
export function applyTheme(name: ThemeName, opts?: { persist?: boolean }): void {
  if (typeof document === 'undefined') return;
  const target = isThemeName(name) ? name : DEFAULT_THEME;
  document.documentElement.setAttribute('data-theme', target);
  // Legacy attributes kept in sync for any CSS still scoped to them.
  document.documentElement.setAttribute('data-chat-theme', target === 'light' ? '' : target);
  document.documentElement.setAttribute('data-task-theme', target === 'light' ? '' : target);
  // shadcn/ui tokens (index.css: --background/--card/--popover/--border …) are
  // keyed on a `.dark` class, NOT data-theme. Keep it in sync so every
  // shadcn-based component follows the theme too.
  document.documentElement.classList.toggle('dark', target !== 'light');
  // `persist: false` applies the theme without recording it as the user's
  // explicit choice (used when following the OS preference).
  if (opts?.persist === false) return;
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, target);
    }
  } catch {
    /* quota / private mode — ignore */
  }
}

/** The OS-level colour-scheme preference (falls back to the default theme). */
export function getSystemTheme(): ThemeName {
  if (typeof window === 'undefined' || !window.matchMedia) return DEFAULT_THEME;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Read the persisted theme, or null if none chosen yet. */
export function getStoredTheme(): ThemeName | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isThemeName(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Decide and apply the initial theme. An explicit stored preference (whose
 * name is currently exposed in THEMES) always wins. With no stored choice we
 * follow the OS colour-scheme preference, but DO NOT persist it — the user
 * stays "unset" (and keeps following the OS) until they toggle explicitly.
 */
export function loadInitialTheme(): ThemeName {
  const stored = getStoredTheme();
  const allowed = stored && THEMES.some(t => t.name === stored) ? stored : null;
  if (allowed) {
    applyTheme(allowed);
    return allowed;
  }
  const sys = getSystemTheme();
  applyTheme(sys, { persist: false });
  return sys;
}

/**
 * Re-apply the OS theme on colour-scheme changes, but only while the user has
 * made no explicit choice. Returns a cleanup function. Safe to call once at
 * app start (app-lifetime listener).
 */
export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent) => {
    if (getStoredTheme()) return; // explicit choice wins
    applyTheme(e.matches ? 'dark' : 'light', { persist: false });
  };
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
